import type { ViewTitleSource } from '@hibro/platform-renderer';
import { surfaceInstanceId } from '@hibro/shared';

/**
 * App-shell registry for iframe `setTitle` (见 3 / 11). Bridges the preload
 * `window.hibroNavigation.onViewSetTitle` fan-out, and the browser runtime's
 * {@link deliverViewTitle}, into the workbench. The latest title per instance
 * is kept so a call that arrives before the workbench subscribes still lands
 * on the panel once it does. Applying it twice is the same write.
 */
class ViewTitleRegistry implements ViewTitleSource {
  private readonly latest = new Map<
    string,
    {
      viewId: string;
      instanceKey: string | undefined;
      title: string;
    }
  >();
  private readonly listeners = new Set<
    (viewId: string, instanceKey: string | undefined, title: string) => void
  >();

  constructor() {
    window.hibroNavigation?.onViewSetTitle?.((viewId, instanceKey, title) =>
      this.deliver(viewId, instanceKey, title),
    );
  }

  deliver(
    viewId: string,
    instanceKey: string | undefined,
    title: string,
  ): void {
    this.latest.set(surfaceInstanceId(viewId, instanceKey), {
      viewId,
      instanceKey,
      title,
    });
    for (const listener of this.listeners) {
      listener(viewId, instanceKey, title);
    }
  }

  subscribe(
    listener: (
      viewId: string,
      instanceKey: string | undefined,
      title: string,
    ) => void,
  ): () => void {
    this.listeners.add(listener);
    for (const pending of this.latest.values()) {
      listener(pending.viewId, pending.instanceKey, pending.title);
    }
    return () => {
      this.listeners.delete(listener);
    };
  }
}

let singleton: ViewTitleRegistry | undefined;

function registry(): ViewTitleRegistry {
  singleton ??= new ViewTitleRegistry();
  return singleton;
}

/** The per-window registry (见 3 / 11). One instance, shared with the workbench. */
export function getViewTitleRegistry(): ViewTitleSource {
  return registry();
}

/**
 * Deliver a title that arrived outside the preload bridge — the browser
 * runtime's host relay (见 18), where there is no `window.hibroNavigation`.
 */
export function deliverViewTitle(
  viewId: string,
  instanceKey: string | undefined,
  title: string,
): void {
  registry().deliver(viewId, instanceKey, title);
}
